// src/pages/AccountPage.tsx
// -----------------------------------------------------------------------------
// The GIDC (customer) account surface - D-033 / D-078.
//
// Signed out -> sign-in / register / reset-password forms.
// Signed in  -> "My Account": details, saved cart, order history.
//
// Registration requires explicit Ts&Cs acceptance (D-079): it is the consent that
// covers the shop owner seeing the customer's cart activity for follow-up. The
// backend refuses a new account without it, so the checkbox is not decoration.
//
// Deliberately kept free of SmartShop-specific business logic (D-078): this is
// account chrome, liftable into a shared GIDC app when a second consumer needs it.
//
// House rule: short hyphen "-" only.
// -----------------------------------------------------------------------------
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { shopConfig as cfg } from '../shopConfig';
import { useBrand } from '../hooks/useBrand';
import { useAuth } from '../hooks/useAuth';

const money = (cents: number | null, ccy = 'EUR') =>
  cents == null ? '' : new Intl.NumberFormat('en-IE', { style: 'currency', currency: ccy }).format(cents / 100);

type Mode = 'signin' | 'register' | 'reset';

export const AccountPage: React.FC = () => {
  const brand = useBrand();
  const accent = brand.accent || cfg.brand.accent;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { account, checking, busy, error, signedIn, register, signIn, signOut, resetPassword } = useAuth();

  // ?return=/cart - same-site paths only (leading single slash), never an open redirect.
  const returnTo = searchParams.get('return');
  const safeReturn = returnTo && /^\/[^/]/.test(returnTo) ? returnTo : null;

  useEffect(() => {
    if (signedIn && safeReturn) navigate(safeReturn, { replace: true });
  }, [signedIn, safeReturn, navigate]);

  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [terms, setTerms] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const input: React.CSSProperties = {
    width: '100%', padding: '12px 14px', fontSize: 15, border: '1px solid rgba(0,0,0,.2)',
    background: '#fff', outline: 'none', fontFamily: 'inherit', marginBottom: 12,
  };
  const primaryBtn: React.CSSProperties = {
    width: '100%', padding: 14, fontSize: 13, fontWeight: 800, textTransform: 'uppercase',
    letterSpacing: '.12em', border: 'none', background: accent, color: cfg.brand.secondary,
    cursor: busy ? 'wait' : 'pointer', opacity: busy ? .7 : 1,
  };
  const linkBtn: React.CSSProperties = {
    background: 'none', border: 'none', color: cfg.brand.secondary, textDecoration: 'underline',
    fontSize: 13, cursor: 'pointer', padding: 0,
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotice(null);
    if (mode === 'register') {
      await register(email, password, name, terms);
    } else if (mode === 'signin') {
      await signIn(email, password);
    } else {
      const ok = await resetPassword(email);
      if (ok) setNotice('If that email has an account, a reset link is on its way.');
    }
  };

  if (checking) {
    return <div style={{ padding: 48, textAlign: 'center', color: 'rgba(0,0,0,.4)' }}>Loading...</div>;
  }

  // ---------------------------------------------------------------- signed in
  if (signedIn && account) {
    const sc = account.saved_cart;
    return (
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '24px 16px 64px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h1 style={{ fontSize: 'clamp(22px,5vw,30px)', fontWeight: 900, fontStyle: 'italic', color: cfg.brand.secondary, margin: 0 }}>
            My Account
          </h1>
          <button onClick={signOut} style={linkBtn}>Sign out</button>
        </div>

        <section style={{ marginTop: 24, background: '#fff', border: '1px solid rgba(0,0,0,.08)', boxShadow: '0 2px 10px rgba(0,0,0,.08)', padding: 20 }}>
          <h2 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.12em', color: 'rgba(0,0,0,.45)', margin: '0 0 12px' }}>Details</h2>
          {account.name && <p style={{ margin: '0 0 6px', fontSize: 15, color: cfg.brand.secondary }}>{account.name}</p>}
          <p style={{ margin: '0 0 6px', fontSize: 15, color: 'rgba(0,0,0,.7)' }}>{account.email}</p>
        </section>

        <section style={{ marginTop: 20, background: '#fff', border: '1px solid rgba(0,0,0,.08)', boxShadow: '0 2px 10px rgba(0,0,0,.08)', padding: 20 }}>
          <h2 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.12em', color: 'rgba(0,0,0,.45)', margin: '0 0 12px' }}>Saved Cart</h2>
          {sc && sc.items.length ? (
            <>
              {sc.items.map((i, n) => (
                <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid rgba(0,0,0,.06)' }}>
                  {i.image_snapshot
                    ? <img src={i.image_snapshot} alt={i.name_snapshot} style={{ width: 48, height: 60, objectFit: 'cover', flex: '0 0 auto', background: '#f2efe6' }} />
                    : <div style={{ width: 48, height: 60, background: '#f2efe6', flex: '0 0 auto' }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 14, color: cfg.brand.secondary, fontWeight: 600 }}>
                      {i.name_snapshot}{i.variant_name_snapshot ? ` - ${i.variant_name_snapshot}` : ''}
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: 'rgba(0,0,0,.5)' }}>Qty {i.qty}</p>
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: accent }}>
                    {money((i.variable_amount ?? i.price_snapshot) * i.qty)}
                  </span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: cfg.brand.secondary }}>Total {money(sc.total)}</span>
                <button onClick={() => navigate('/cart')} style={{ background: accent, color: cfg.brand.secondary, border: 'none', padding: '10px 18px', fontWeight: 800, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.1em', cursor: 'pointer' }}>
                  Go to Cart
                </button>
              </div>
            </>
          ) : (
            <p style={{ margin: 0, fontSize: 14, color: 'rgba(0,0,0,.5)' }}>Nothing saved yet.</p>
          )}
        </section>

        <section style={{ marginTop: 20, background: '#fff', border: '1px solid rgba(0,0,0,.08)', boxShadow: '0 2px 10px rgba(0,0,0,.08)', padding: 20 }}>
          <h2 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.12em', color: 'rgba(0,0,0,.45)', margin: '0 0 12px' }}>Order History</h2>
          {account.orders && account.orders.length ? (
            account.orders.map((o: any, n: number) => (
              <div key={n} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(0,0,0,.06)' }}>
                <span style={{ fontSize: 14, color: cfg.brand.secondary }}>{o.created_at ? String(o.created_at).slice(0, 10) : 'Order'}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: accent }}>{money(o.total)}</span>
              </div>
            ))
          ) : (
            <p style={{ margin: 0, fontSize: 14, color: 'rgba(0,0,0,.5)' }}>No orders yet.</p>
          )}
        </section>
      </div>
    );
  }

  // --------------------------------------------------------------- signed out
  return (
    <div style={{ maxWidth: 420, margin: '0 auto', padding: '32px 16px 64px' }}>
      <h1 style={{ fontSize: 'clamp(22px,5vw,28px)', fontWeight: 900, fontStyle: 'italic', color: cfg.brand.secondary, margin: '0 0 6px' }}>
        {mode === 'register' ? 'Create Account' : mode === 'reset' ? 'Reset Password' : 'Sign In'}
      </h1>
      <p style={{ fontSize: 14, color: 'rgba(0,0,0,.55)', margin: '0 0 22px' }}>
        {mode === 'register'
          ? 'Save your cart, see your orders, and check out faster.'
          : mode === 'reset'
            ? 'Enter your email and we will send a reset link.'
            : `Welcome back to ${cfg.shop_name}.`}
      </p>

      <form onSubmit={submit}>
        {mode === 'register' && (
          <input style={input} type="text" placeholder="Your name" value={name}
            onChange={e => setName(e.target.value)} autoComplete="name" />
        )}
        <input style={input} type="email" placeholder="Email" value={email} required
          onChange={e => setEmail(e.target.value)} autoComplete="email" />
        {mode !== 'reset' && (
          <input style={input} type="password" placeholder="Password" value={password} required
            onChange={e => setPassword(e.target.value)}
            autoComplete={mode === 'register' ? 'new-password' : 'current-password'} />
        )}

        {mode === 'register' && (
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '4px 0 16px', fontSize: 13, color: 'rgba(0,0,0,.7)', lineHeight: 1.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={terms} onChange={e => setTerms(e.target.checked)}
              style={{ marginTop: 3, flex: '0 0 auto' }} />
            <span>
              I accept the <a href="/terms" style={{ color: cfg.brand.secondary }}>terms</a> and{' '}
              <a href="/privacy" style={{ color: cfg.brand.secondary }}>privacy policy</a>, and agree that{' '}
              {cfg.shop_name} may see my cart and order activity to help me with my purchase.
            </span>
          </label>
        )}

        {error && <p style={{ color: '#b3261e', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
        {notice && <p style={{ color: cfg.brand.secondary, fontSize: 13, margin: '0 0 12px' }}>{notice}</p>}

        <button type="submit" disabled={busy} style={primaryBtn}>
          {busy ? 'Please wait...' : mode === 'register' ? 'Create Account' : mode === 'reset' ? 'Send Reset Link' : 'Sign In'}
        </button>
      </form>

      <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
        {mode === 'signin' && (
          <>
            <button type="button" style={linkBtn} onClick={() => { setMode('register'); setNotice(null); }}>New here? Create an account</button>
            <button type="button" style={linkBtn} onClick={() => { setMode('reset'); setNotice(null); }}>Forgot your password?</button>
          </>
        )}
        {mode === 'register' && (
          <button type="button" style={linkBtn} onClick={() => { setMode('signin'); setNotice(null); }}>Already have an account? Sign in</button>
        )}
        {mode === 'reset' && (
          <button type="button" style={linkBtn} onClick={() => { setMode('signin'); setNotice(null); }}>Back to sign in</button>
        )}
      </div>
    </div>
  );
};

export default AccountPage;